const cache = window.localStorage;

window.TTL_MINUTE = 60 * 1000;
window.TTL_HOUR = window.TTL_MINUTE * 60;
window.TTL_DAY = window.TTL_HOUR * 24;
window.TTL_WEEK = window.TTL_DAY * 7 * 24;

// Cache is enabled by default, but can be disabled via UI preference
window.DISABLED_CACHE = false;

const isCacheDisabled = () => {
    try {
        if (window.DISABLED_CACHE) return true;
        const saved = cache.getItem('offline-cache-enabled');
        if (saved !== null) {
            return !JSON.parse(saved);
        }
    } catch (e) {
        console.error('Error checking offline cache enabled state:', e);
    }
    return false;
};

window.localCache = (name, autoClean) => {
    const prefix = name + "::";
    const memStore = new Map();
    
    const instance = {
        setItem(key, value, ttl) {
            try {
                if (isCacheDisabled()) {
                    this.clear();
                    return;
                }
                ttl = ttl || window.TTL_DAY;
                const expiration = Date.now() + ttl;
                memStore.set(key, { value, expiration });

                // If key is a song or song-related key, do NOT persist in localStorage to avoid QuotaExceededError (~5MB limit).
                // Songs and large payloads are stored in IndexedDB (dbService) instead.
                const isSong = key.includes('::song::') || key.startsWith('song::');
                if (isSong) {
                    return;
                }

                const serialized = JSON.stringify({ value, expiration });
                // Skip localStorage if item itself is larger than 25KB
                if (serialized.length > 25000) {
                    return;
                }

                cache.setItem(prefix + key, serialized);
            } catch (e) {
                console.warn('[localCache] Item not saved to localStorage: ' + key, e.message);
                // Handle local storage full by cleaning only expired items rather than wiping the entire cache
                if (e.name === 'QuotaExceededError' || e.name === 'NS_ERROR_DOM_QUOTA_REACHED') {
                    try {
                        Object.keys(cache).forEach(k => {
                            if (k.startsWith(prefix)) {
                                const localKey = k.slice(prefix.length);
                                instance.getItem(localKey); // getItem auto-removes expired entries
                            }
                        });
                    } catch (cleanErr) {
                        console.warn('[localCache] Error pruning expired items:', cleanErr);
                    }
                }
            }
        },

        getItem(key) {
            try {
                if (isCacheDisabled()) {
                    this.clear();
                    return null;
                }

                if (memStore.has(key)) {
                    const memItem = memStore.get(key);
                    if (memItem.expiration > Date.now()) {
                        return memItem.value;
                    }
                    memStore.delete(key);
                }

                const item = cache.getItem(prefix + key);
                if (!item) return null;

                const parsed = JSON.parse(item);
                if (!parsed || typeof parsed !== 'object' || !('expiration' in parsed)) {
                    return parsed; // Fallback if format is simple/unexpected
                }

                if (parsed.expiration <= Date.now()) {
                    this.removeItem(key);
                    return null;
                }
                memStore.set(key, parsed);
                return parsed.value;
            } catch (e) {
                return null;
            }
        },

        removeItem(key) {
            memStore.delete(key);
            cache.removeItem(prefix + key);
        },

        clear() {
            memStore.clear();
            Object.keys(cache).forEach(key => {
                if (key.startsWith(prefix)) {
                    cache.removeItem(key);
                }
            });
        }
    };

    if (autoClean) {
        // Run cleaning task every 5 minutes instead of 1 second to save CPU
        setInterval(() => {
            if (isCacheDisabled()) {
                instance.clear();
                return;
            }
            Object.keys(cache).forEach(key => {
                if (key.startsWith(prefix)) {
                    const localKey = key.slice(prefix.length);
                    instance.getItem(localKey); // Auto-deletes if expired
                }
            });
        }, 5 * window.TTL_MINUTE);
    }

    return instance;
};
